import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { staffApi } from '@/services/api';
import { downloadCSV, readCSVFile, importCSV } from '@/utils/csv';
import { HiOutlinePlus, HiOutlineSearch, HiOutlineEye, HiOutlineDownload, HiOutlineUpload } from 'react-icons/hi';

interface StaffMember {
  id: string;
  staffNumber: string;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  employmentType: string;
  position: string;
  hireDate: string;
  status: string;
}

interface StaffResponse {
  data: StaffMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

const employmentLabels: Record<string, string> = {
  full_time: '正社員',
  part_time: 'パート',
  contract: '契約社員',
  temporary: '派遣',
};

export default function StaffPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<StaffResponse>({
    queryKey: ['staff', { page, search }],
    queryFn: () => staffApi.list({ page, search }) as Promise<StaffResponse>,
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadCSV('/api/v1/export/staff');
      toast.success('CSVをダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const csvText = await readCSVFile(file);
      const result = await importCSV('/api/v1/import/staff', csvText);
      
      if (result.errors?.length > 0) {
        toast.error(`${result.imported}件インポート、${result.errors.length}件エラー`);
        console.error('Import errors:', result.errors);
      } else {
        toast.success(`${result.imported}件インポートしました`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    } catch (error) {
      toast.error('インポートに失敗しました');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const staffList = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">職員管理</h1>
          <p className="text-gray-600 mt-1">職員の情報を管理します</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary"
          >
            <HiOutlineDownload className="w-5 h-5 mr-2" />
            {isExporting ? 'エクスポート中...' : 'CSVエクスポート'}
          </button>
          <button 
            onClick={handleImportClick}
            disabled={isImporting}
            className="btn-secondary"
          >
            <HiOutlineUpload className="w-5 h-5 mr-2" />
            {isImporting ? 'インポート中...' : 'CSVインポート'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
          <Link to="/staff/new" className="btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            新規登録
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="relative flex-1 max-w-md">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="名前で検索..."
              className="input pl-10"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>職員番号</th>
                <th>氏名</th>
                <th>雇用形態</th>
                <th>役職</th>
                <th>入社日</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : staffList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    職員が見つかりません
                  </td>
                </tr>
              ) : (
                staffList.map((staff) => (
                  <tr key={staff.id}>
                    <td className="font-mono text-sm">{staff.staffNumber}</td>
                    <td>
                      <div>
                        <p className="font-medium text-gray-900">
                          {staff.lastName} {staff.firstName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {staff.lastNameKana} {staff.firstNameKana}
                        </p>
                      </div>
                    </td>
                    <td>{employmentLabels[staff.employmentType] || staff.employmentType}</td>
                    <td>{staff.position || '-'}</td>
                    <td>{staff.hireDate || '-'}</td>
                    <td>
                      <span className={staff.status === 'active' ? 'badge-success' : 'badge-gray'}>
                        {staff.status === 'active' ? '在籍' : '退職'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/staff/${staff.id}`} className="btn-ghost text-sm py-1 px-2">
                        <HiOutlineEye className="w-4 h-4 mr-1" />
                        詳細
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > pagination.limit && (
          <div className="card-body border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {pagination.total}件中 {(pagination.page - 1) * pagination.limit + 1}-
                {Math.min(pagination.page * pagination.limit, pagination.total)}件を表示
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn-secondary text-sm"
                >
                  前へ
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * pagination.limit >= pagination.total}
                  className="btn-secondary text-sm"
                >
                  次へ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
